import { Check, TrendingUp } from "lucide-react";
import { Reveal, SectionHead, Avatar, Phone, StatusBar } from "../components/bits";

const StepVisual = ({ n }) => {
    if (n === 1)
        return (
            <div className="grid h-36 grid-cols-6 place-items-center gap-1.5 rounded-xl border border-cream/10 bg-matte p-4">
                {Array.from({ length: 24 }).map((_, i) => (
                    <span
                        key={i}
                        className={`h-3.5 w-3.5 rounded-full ${
                            [2, 7, 13, 19].includes(i)
                                ? "animate-pulse-soft bg-rose ring-2 ring-rose/30"
                                : "bg-cream/12"
                        }`}
                        style={{ animationDelay: `${i * 0.25}s` }}
                    />
                ))}
            </div>
        );
    if (n === 2)
        return (
            <div className="flex h-36 flex-wrap content-center items-center justify-center gap-1.5 rounded-xl border border-cream/10 bg-matte p-4">
                {["Lip filler", "Skin", "Laser", "Body", "Overdue", "Cancelled"].map((t, i) => (
                    <span
                        key={t}
                        className={`animate-float-soft rounded-full border px-2.5 py-1 font-mono text-[8px] uppercase tracking-[0.14em] ${
                            i < 4 ? "border-gold/30 bg-charcoal text-gold" : "border-rose/40 bg-blush/10 text-rose"
                        }`}
                        style={{ animationDelay: `${i * 0.5}s` }}
                    >
                        {t}
                    </span>
                ))}
            </div>
        );
    if (n === 3)
        return (
            <div className="flex h-36 items-center justify-center rounded-xl border border-cream/10 bg-matte p-3">
                <Phone className="w-[104px]">
                    <StatusBar />
                    <div className="px-2 pb-3 pt-1">
                        <div className="rounded-lg rounded-tl-sm bg-white p-2">
                            <p className="font-sans text-[7.5px] leading-snug text-ink">
                                Hi Sarah — time for your lip filler refresh?
                            </p>
                        </div>
                        <div className="mt-1.5 flex items-center justify-center gap-1 rounded bg-blush py-1">
                            <Check className="h-2.5 w-2.5 text-ink" strokeWidth={2.5} />
                            <span className="font-mono text-[6.5px] uppercase tracking-[0.14em] text-ink">Follow-up sent ✓</span>
                        </div>
                    </div>
                </Phone>
            </div>
        );
    if (n === 4)
        return (
            <div className="flex h-36 flex-col items-center justify-center gap-2 rounded-xl border border-cream/10 bg-matte p-4">
                <div className="grid grid-cols-7 gap-1">
                    {Array.from({ length: 21 }).map((_, i) => (
                        <span
                            key={i}
                            className={`h-3.5 w-3.5 rounded font-mono text-[6px] ${
                                i === 12 ? "animate-glow bg-gold/80" : "bg-cream/10"
                            }`}
                        />
                    ))}
                </div>
                <span className="flex items-center gap-1.5 rounded-full bg-cream px-3 py-1 font-mono text-[8px] uppercase tracking-[0.18em] text-ink">
                    Booked <Check className="h-3 w-3 text-gold" strokeWidth={2.5} />
                </span>
            </div>
        );
    return (
        <div className="flex h-36 flex-col justify-center gap-1.5 rounded-xl border border-cream/10 bg-matte p-4">
            {["Clients contacted", "Clients reactivated", "Appointments booked", "Revenue recovered"].map((t, i) => (
                <div key={t} className="flex items-center gap-2">
                    <span className={`h-1.5 w-1.5 rounded-full ${i === 3 ? "bg-gold" : "bg-rose/70"}`} />
                    <span className="font-mono text-[8px] uppercase tracking-[0.14em] text-cream/70">{t}</span>
                    <span className="ml-auto h-1 rounded-full bg-gradient-to-r from-rose/60 to-gold/80" style={{ width: `${88 - i * 18}%`, maxWidth: "40%" }} />
                    {i === 3 && <TrendingUp className="h-3 w-3 text-gold" />}
                </div>
            ))}
        </div>
    );
};

const STEPS = [
    { n: 1, title: "Find the Opportunity", copy: "We identify the past clients worth following up based on things like their last visit, previous treatment and time since booking." },
    { n: 2, title: "Segment Properly", copy: "Clients are grouped by treatment, timing and history so the right people get the right message." },
    { n: 3, title: "Follow Up Automatically", copy: "A personalised message goes out at the right moment — written around your clinic’s tone." },
    { n: 4, title: "Make Rebooking Easy", copy: "The client replies, picks a time, and the appointment lands back on your calendar." },
    { n: 5, title: "Track the Result", copy: "See exactly which clients came back, which appointments were booked and what revenue was recovered." },
];

export const HowItWorks = () => (
    <section id="how-it-works" data-testid="how-it-works" className="grain relative overflow-hidden bg-obsidian py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <SectionHead
                dark
                eyebrow="How it works"
                title="From dormant database to booked appointments."
            />
            <div className="relative mt-12">
                <div className="absolute left-0 right-0 top-5 hidden h-px lg:block">
                    <svg className="h-2 w-full" preserveAspectRatio="none">
                        <line x1="0" y1="4" x2="100%" y2="4" stroke="#CBA368" strokeOpacity="0.35" strokeWidth="1" className="line-flow" />
                    </svg>
                </div>
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-5 lg:gap-4">
                    {STEPS.map((s, i) => (
                        <Reveal key={s.n} delay={i * 0.1}>
                            <article data-testid={`how-step-${s.n}`} className="card-lift relative h-full rounded-2xl border border-cream/10 bg-charcoal/60 p-4">
                                <div className="relative z-10 mb-4 flex h-10 w-10 items-center justify-center rounded-full border border-gold/40 bg-obsidian font-mono text-[11px] tracking-[0.14em] text-gold">
                                    {String(s.n).padStart(2, "0")}
                                </div>
                                <StepVisual n={s.n} />
                                <h3 className="mt-4 font-sans text-[15px] font-medium uppercase tracking-wide text-cream">
                                    {s.title}
                                </h3>
                                <p className="mt-2 text-[13px] leading-relaxed text-cream/55">{s.copy}</p>
                            </article>
                        </Reveal>
                    ))}
                </div>
            </div>
            <Reveal delay={0.2} className="mt-10 flex items-center justify-center gap-3">
                <Avatar initials="S" tone="bg-blush text-ink" />
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-cream/50">
                    One client — moved through the whole system, automatically
                </span>
            </Reveal>
        </div>
    </section>
);
